/*
 * Export helpers for the seven ledger reports of the accountant's document
 * 2026-10-03 (section 2): styled multi-sheet Excel, an A4 print window with
 * the official header block and "page X of N", and the server CSV download.
 *
 * Usage in a page:
 *
 *   import financeReportExport from "@/mixins/financeReportExport";
 *   export default { mixins: [financeReportExport], ... }
 *
 *   const columns = [
 *     { key: "code", label: "الرمز", type: "code", width: 14 },
 *     { key: "name", label: "الاسم", type: "text", width: 40, indent: true },
 *     { key: "debit", label: "مدين", type: "money" },
 *     { key: "count", label: "عدد القيود", type: "count" },
 *   ];
 *   const rows = [
 *     { code: "1", name: "الأصول", debit: 150000, count: 3, _kind: "subtotal", _level: 0 },
 *     { code: "121101", name: "الصندوق", debit: 100000, count: 2, _level: 2, _highlight: "warn" },
 *   ];
 *   // header = the backend headerBlock (company_name, title, period, currency,
 *   // currency_label, issued_at) or this.frBuildHeader({ title, period }).
 *   this.frExportXlsx({ fileName: "accounting-balance", header, sheets: [{ name: "التقرير", columns, rows }] });
 *   this.frPrint({ header, sections: [{ title: "SYP", columns, rows, notes: [] }], orientation: "portrait" });
 *   this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_ACCOUNTING_BALANCE, params, "accounting-balance");
 *
 * Column types: money (STORED old-unit amount: divided by 100 here, the house
 * display convention -- never send divided values back to the server),
 * number, count, percent, text, code (LTR), date.
 * Row meta keys (never columns): _kind = header | section | subtotal | total |
 * opening | closing | detail, _level = indent depth for the `indent` column,
 * _highlight = warn | error | ok, _bold = true. A row may also be an ARRAY
 * aligned to the columns.
 */

import { companyNow, companyToday } from "@/util/companyTime";

const MONEY_FORMAT = "#,##0.00";
const COUNT_FORMAT = "#,##0";

const KIND_FILL = {
  header: "ECEFF1",
  section: "E3F2FD",
  subtotal: "F5F5F5",
  total: "E8F5E9",
  opening: "FAFAFA",
  closing: "FAFAFA",
};

const HIGHLIGHT_FILL = {
  warn: "FFE0B2",
  error: "FFCDD2",
  ok: "E8F5E9",
};

const BOLD_KINDS = ["header", "section", "subtotal", "total", "opening", "closing"];

export default {
  methods: {
    frIsArabic() {
      return !!(this.$i18n && this.$i18n.locale === "ar");
    },

    frT(key) {
      return this.$t("finance.reports2.common." + key);
    },

    // Final-version doc 2026-10-05: export stamps use the company clock
    // (Damascus, fixed UTC+3), never the PC's zone.
    frFileStamp() {
      return companyToday();
    },

    /** Company wall-clock "YYYY-MM-DD HH:mm:ss" for a client-built header. */
    frNowStamp() {
      return companyNow().format("YYYY-MM-DD HH:mm:ss");
    },

    /**
     * Header block built client-side when the payload carries none.
     * period: { from, to } or { date }.
     */
    frBuildHeader(options) {
      const opts = options || {};
      return {
        company_name: opts.company_name || this.frT("companyFallback"),
        title: opts.title || "",
        period: opts.period || {},
        currency: opts.currency || "SYP",
        currency_label: opts.currency_label || this.frT("currencySyp"),
        issued_at: opts.issued_at || this.frNowStamp(),
      };
    },

    frPeriodText(period) {
      const p = period || {};
      if (p.date) {
        return String(p.date);
      }
      const parts = [];
      if (p.from) parts.push(this.frT("from") + " " + p.from);
      if (p.to) parts.push(this.frT("to") + " " + p.to);
      return parts.join(" ");
    },

    /** [label, value] pairs of the header block, in print order. */
    frHeaderLines(header) {
      const h = header || this.frBuildHeader();
      return [
        [this.frT("company"), h.company_name || this.frT("companyFallback")],
        [this.frT("period"), this.frPeriodText(h.period)],
        [this.frT("currency"), h.currency_label || h.currency || this.frT("currencySyp")],
        [this.frT("issuedAt"), h.issued_at || this.frNowStamp()],
      ];
    },

    /** Stored old-unit amount -> display number (÷100) for Excel. */
    frMoney(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const n = Number(value);
      return isNaN(n) ? "" : n / 100;
    },

    frCellRaw(column, row, index) {
      if (Array.isArray(row)) {
        return row[index];
      }
      return row ? row[column.key] : undefined;
    },

    /** Excel cell value: numbers stay numbers so Excel can sum them. */
    frCellValue(column, raw) {
      if (raw === null || raw === undefined || raw === "") {
        return "";
      }
      switch (column.type) {
        case "money":
          return this.frMoney(raw);
        case "number":
        case "count":
        case "percent": {
          const n = Number(raw);
          return isNaN(n) ? String(raw) : n;
        }
        default:
          return String(raw);
      }
    },

    /** Text for tables and the print window. */
    frCellText(column, raw) {
      if (raw === null || raw === undefined || raw === "") {
        return column.type === "money" ? "—" : "";
      }
      switch (column.type) {
        case "money":
          return this.formatMoney(raw);
        case "count":
        case "number": {
          const n = Number(raw);
          return isNaN(n) ? String(raw) : new Intl.NumberFormat("en").format(n);
        }
        case "percent":
          return String(raw) + "%";
        default:
          return String(raw);
      }
    },

    frIsNumericColumn(column) {
      return ["money", "number", "count", "percent"].indexOf(column.type) !== -1;
    },

    frSheetName(name, used) {
      let base = String(name || "Sheet").replace(/[\][:*?/\\]/g, " ").trim().slice(0, 31) || "Sheet";
      let candidate = base;
      let n = 2;
      while (used[candidate]) {
        const suffix = " (" + n++ + ")";
        candidate = base.slice(0, 31 - suffix.length) + suffix;
      }
      used[candidate] = true;
      return candidate;
    },

    /**
     * Styled .xlsx built client-side (lazy xlsx-js-style chunk, like the
     * trial balance). One sheet per entry of `sheets`; each sheet starts with
     * the header block (unless sheet.headerBlock === false), then the column
     * header row, then the rows.
     *
     * options: { fileName, header, sheets: [{ name, title?, columns, rows, notes?, headerBlock? }] }
     * Returns a Promise; a failure shows the standard red toast.
     */
    frExportXlsx(options) {
      const opts = options || {};
      const header = opts.header || this.frBuildHeader();
      const sheets = opts.sheets || [];
      const isArabic = this.frIsArabic();

      return import(/* webpackChunkName: "xlsx" */ "xlsx-js-style")
        .then((module) => {
          const XLSX = module.default || module;
          const thin = { style: "thin", color: { rgb: "9E9E9E" } };
          const borders = { top: thin, bottom: thin, left: thin, right: thin };
          const wb = XLSX.utils.book_new();
          const usedNames = {};

          sheets.forEach((sheet) => {
            const columns = sheet.columns || [];
            const rows = sheet.rows || [];
            const width = Math.max(columns.length, 2);
            const aoa = [];
            const merges = [];
            const meta = []; // per aoa row: { role, row? }

            if (sheet.headerBlock !== false) {
              aoa.push([header.title || sheet.title || ""]);
              meta.push({ role: "title" });
              merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: width - 1 } });
              this.frHeaderLines(header).forEach((pair) => {
                aoa.push([pair[0], pair[1]]);
                meta.push({ role: "meta" });
                merges.push({ s: { r: aoa.length - 1, c: 1 }, e: { r: aoa.length - 1, c: width - 1 } });
              });
              if (sheet.title && sheet.title !== header.title) {
                aoa.push([sheet.title]);
                meta.push({ role: "title" });
                merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: width - 1 } });
              }
              aoa.push([]);
              meta.push({ role: "blank" });
            }

            if (columns.length) {
              aoa.push(columns.map((column) => column.label || column.key));
              meta.push({ role: "columns" });
            }

            rows.forEach((row) => {
              aoa.push(columns.map((column, index) => this.frCellValue(column, this.frCellRaw(column, row, index))));
              meta.push({ role: "row", row: row });
            });

            (sheet.notes || []).forEach((note) => {
              aoa.push([typeof note === "string" ? note : note.ar || note.text || ""]);
              meta.push({ role: "note" });
              merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: width - 1 } });
            });

            const ws = XLSX.utils.aoa_to_sheet(aoa.length ? aoa : [[""]]);
            ws["!merges"] = merges;
            ws["!cols"] = columns.map((column) => ({
              wch: column.width || (this.frIsNumericColumn(column) ? 16 : column.type === "code" ? 14 : 28),
            }));

            meta.forEach((info, r) => {
              const cellCount = info.role === "row" || info.role === "columns" ? columns.length : width;
              for (let c = 0; c < cellCount; c++) {
                const ref = XLSX.utils.encode_cell({ r: r, c: c });
                const cell = ws[ref];
                if (!cell) {
                  continue;
                }
                if (info.role === "title") {
                  cell.s = { font: { bold: true, sz: 14 }, alignment: { horizontal: "center" } };
                } else if (info.role === "meta") {
                  cell.s = { font: { bold: c === 0 } };
                } else if (info.role === "note") {
                  cell.s = { font: { italic: true, color: { rgb: "555555" } }, alignment: { wrapText: true } };
                } else if (info.role === "columns") {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: KIND_FILL.header } },
                    border: borders,
                    alignment: { vertical: "center", wrapText: true },
                  };
                } else if (info.role === "row") {
                  const row = info.row || {};
                  const column = columns[c];
                  const kind = row._kind || "detail";
                  const style = {
                    font: { bold: !!row._bold || BOLD_KINDS.indexOf(kind) !== -1 },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  const fill = HIGHLIGHT_FILL[row._highlight] || KIND_FILL[kind];
                  if (fill) {
                    style.fill = { patternType: "solid", fgColor: { rgb: fill } };
                  }
                  if (column.indent && row._level) {
                    style.alignment.indent = Math.min(Number(row._level) || 0, 15);
                  }
                  if (column.type === "money" && typeof cell.v === "number") {
                    cell.z = MONEY_FORMAT;
                  } else if (column.type === "count" && typeof cell.v === "number") {
                    cell.z = COUNT_FORMAT;
                  }
                  cell.s = style;
                }
              }
            });

            XLSX.utils.book_append_sheet(wb, ws, this.frSheetName(sheet.name || sheet.title, usedNames));
          });

          if (!sheets.length) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([[""]]), "Sheet");
          }
          wb.Workbook = { Views: [{ RTL: isArabic }] };
          XLSX.writeFile(wb, (opts.fileName || "report") + "-" + this.frFileStamp() + ".xlsx");
        })
        .catch(() => {
          window.getApp.$emit("SHOW_TOAST", this.frT("failedExport"), "red");
        });
    },

    frEscape(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    /** HTML <table> for one print section. */
    frPrintTable(columns, rows) {
      const esc = this.frEscape;
      const head = columns
        .map((column) => '<th class="' + (this.frIsNumericColumn(column) ? "num" : "") + '">' + esc(column.label || column.key) + "</th>")
        .join("");
      const body = rows
        .map((row) => {
          const classes = [];
          const kind = (row && row._kind) || "detail";
          classes.push("k-" + kind);
          if (row && row._highlight) classes.push("hl-" + row._highlight);
          if (row && row._bold) classes.push("bold");
          const cells = columns
            .map((column, index) => {
              const text = this.frCellText(column, this.frCellRaw(column, row, index));
              const cls = this.frIsNumericColumn(column) ? "num" : column.type === "code" ? "code" : "";
              const pad = column.indent && row && row._level ? ' style="padding-inline-start:' + (8 + Number(row._level) * 14) + 'px"' : "";
              return '<td class="' + cls + '"' + pad + ">" + esc(text) + "</td>";
            })
            .join("");
          return '<tr class="' + classes.join(" ") + '">' + cells + "</tr>";
        })
        .join("");
      return "<table><thead><tr>" + head + "</tr></thead><tbody>" + body + "</tbody></table>";
    },

    /**
     * A4 print window (browser "Save as PDF" renders the Arabic faithfully):
     * header block (company, title, period, currency, issue date) and
     * "صفحة X من N" in the page margin through CSS counters.
     *
     * options: { header, sections: [{ title?, columns, rows, notes? }], notes?, orientation: "portrait"|"landscape", html? }
     * `html` (optional) is appended raw after the sections for custom blocks.
     */
    frPrint(options) {
      const opts = options || {};
      const header = opts.header || this.frBuildHeader();
      const isArabic = this.frIsArabic();
      const esc = this.frEscape;
      const orientation = opts.orientation === "landscape" ? "landscape" : "portrait";
      const pageWord = this.frT("page");
      const ofWord = this.frT("of");

      const metaHtml = this.frHeaderLines(header)
        .map((pair) => "<div><b>" + esc(pair[0]) + ":</b> " + esc(pair[1]) + "</div>")
        .join("");

      const sectionsHtml = (opts.sections || [])
        .map((section) => {
          const notes = (section.notes || [])
            .map((note) => '<p class="note">' + esc(typeof note === "string" ? note : note.ar || note.text || "") + "</p>")
            .join("");
          return (
            '<section>' +
            (section.title ? "<h2>" + esc(section.title) + "</h2>" : "") +
            this.frPrintTable(section.columns || [], section.rows || []) +
            notes +
            "</section>"
          );
        })
        .join("");

      const notesHtml = (opts.notes || [])
        .map((note) => '<p class="note">' + esc(typeof note === "string" ? note : note.ar || note.text || "") + "</p>")
        .join("");

      const pageContent = '"' + pageWord + ' " counter(page) " ' + ofWord + ' " counter(pages)';

      const doc =
        "<!doctype html>" +
        '<html dir="' + (isArabic ? "rtl" : "ltr") + '" lang="' + (isArabic ? "ar" : "en") + '">' +
        '<head><meta charset="utf-8"><title>' + esc(header.title || "") + "</title><style>" +
        'body{font-family:"Segoe UI",Tahoma,Arial,sans-serif;color:#111;margin:0;padding:0 4px;}' +
        "header{border-bottom:2px solid #111;padding-bottom:8px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:flex-end;gap:16px;}" +
        "h1{font-size:19px;margin:0 0 4px;}" +
        "h2{font-size:15px;margin:14px 0 6px;}" +
        ".company{font-size:15px;font-weight:700;}" +
        ".meta{font-size:12px;color:#333;text-align:end;line-height:1.6;}" +
        "table{width:100%;border-collapse:collapse;font-size:11.5px;}" +
        "th,td{border:1px solid #888;padding:3px 6px;text-align:start;vertical-align:top;}" +
        "thead{display:table-header-group;}" +
        "thead th{background:#eceff1;font-weight:700;}" +
        "tr{page-break-inside:avoid;}" +
        ".num{text-align:end;direction:ltr;unicode-bidi:embed;white-space:nowrap;}" +
        ".code{white-space:nowrap;direction:ltr;unicode-bidi:embed;}" +
        ".k-section td{background:#e3f2fd;font-weight:700;}" +
        ".k-subtotal td,.k-opening td,.k-closing td,.bold td{background:#f5f5f5;font-weight:700;}" +
        ".k-total td{background:#e8f5e9;font-weight:700;}" +
        ".hl-warn td{background:#ffe0b2;}" +
        ".hl-error td{background:#ffcdd2;}" +
        ".hl-ok td{background:#e8f5e9;}" +
        ".note{font-size:11px;color:#555;margin:4px 0;}" +
        "@page{size:A4 " + orientation + ";margin:12mm 10mm 14mm;" +
        "@bottom-center{content:" + pageContent + ";font-size:10px;color:#555;}}" +
        "</style></head><body>" +
        "<header><div>" +
        '<div class="company">' + esc(header.company_name || this.frT("companyFallback")) + "</div>" +
        "<h1>" + esc(header.title || "") + "</h1></div>" +
        '<div class="meta">' + metaHtml + "</div></header>" +
        sectionsHtml +
        (opts.html || "") +
        notesHtml +
        "</body></html>";

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        window.getApp.$emit("SHOW_TOAST", this.frT("popupBlocked"), "red");
        return;
      }
      printWindow.document.write(doc);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(function () {
        printWindow.print();
      }, 400);
    },

    frErrorMessage(error, fallback) {
      if (error && error.response && error.response.data) {
        const data = error.response.data;
        if (data.errors && typeof data.errors === "object") {
          const first = Object.keys(data.errors)[0];
          if (first && data.errors[first] && data.errors[first][0]) {
            return data.errors[first][0];
          }
        }
        if (data.message) {
          return data.message;
        }
      }
      return fallback || this.frT("failedLoad");
    },

    /**
     * Server CSV (UTF-8 BOM) of the same endpoint: GET url with
     * params + format=csv, saved as <baseName>-<date>.csv.
     */
    frDownloadCsv(url, params, baseName) {
      const query = Object.assign({}, params || {}, { format: "csv" });
      return this.axios
        .get(url, { params: query, responseType: "blob" })
        .then((response) => {
          const blob = new Blob([response.data], { type: response.headers["content-type"] || "text/csv" });
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = (baseName || "report") + "-" + this.frFileStamp() + ".csv";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((error) => {
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.frT("failedExport")), "red");
        });
    },
  },
};
